// ─────────────────────────────────────────────────────────────
// ScriptMetadataEditDialog
//
// Single edit dialog used from both the My Scripts tab and the
// author's own card on the Community tab. Lets the owner change:
//   • Title (required)
//   • Description (optional, multiline)
//   • Paired reference image (optional; can be cleared)
//
// Behavior:
//   • Pre-fills from the current script — title, description,
//     default_image_id all pre-set when the dialog opens.
//   • Tracks per-field "touched" state so the PUT payload only
//     contains fields the user actually changed. This honors Sam's
//     three-state semantics on `default_image_id` (absent = no
//     change, explicit null = clear, int = set).
//   • Save → PUT /api/scripts/:id via userScriptsApi.update.
//   • On 403/422/other errors: inline error banner, dialog stays
//     open so the user can retry.
//   • On success: closes itself and calls `onSuccess(updated)`
//     with the server-returned script so the parent can update
//     local state without a full refetch (parents may still
//     refetch for safety).
//
// Image picker UX (2026-05-15 — unified with SaveScriptModal +
// PublishScriptDialog):
//   • Replaced the segmented My Images / Shared tabs + scrolling
//     grid with the shared `PairedImageRow` (thumbnail + Change /
//     Remove). Click Change → `LibrarySelectModal` opens on top of
//     this dialog. On pick, the row updates and the dialog body
//     stays put — no re-mount, no scroll reset.
//   • Mirrors PublishScriptDialog's stacking pattern so the three
//     dialogs that touch script-image pairing all behave the same
//     way.
//
// Code is NOT edited here. The 2026-05-11 version-pinning model
// still applies — code-editing goes through PythonTab + the
// save-version flow. This dialog is metadata-only.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { imageApi, userScriptsApi } from "../../api/client";
import type {
  LibraryImage,
  UserImage,
  UserScript,
  UserScriptUpdatePayload,
} from "../../api/client";
import type { PlaceholderImage } from "./types";
import { LibrarySelectModal, PairedImageRow } from "./modals";

// ── Types ─────────────────────────────────────────────────────

export interface ScriptMetadataEditDialogProps {
  open: boolean;
  /** The script being edited. Null when the dialog is closed. */
  script: UserScript | null;
  onClose: () => void;
  /** Fired after a successful PUT with the updated script payload. */
  onSuccess: (updated: UserScript) => void;
}

// ── Helpers ───────────────────────────────────────────────────

/**
 * Shape a `UserImage` or `LibraryImage` (raw API record) into the
 * `PlaceholderImage` shape that `PairedImageRow` expects. Mirrors the
 * conversion `LibrarySelectModal.handleSelect` does — kept inline here so
 * the dialog can synthesize the row's input from the pre-selected id.
 */
function toPlaceholderImage(
  img: UserImage | LibraryImage,
  source: "user" | "library",
): PlaceholderImage {
  return {
    id: String(img.id),
    name: img.name,
    description: img.description ?? "",
    tags: img.tags ?? null,
    type: img.image_type as PlaceholderImage["type"],
    source,
    acquisition_metadata: img.acquisition_metadata ?? null,
    metadata_source: img.metadata_source ?? null,
    derived_tags: img.derived_tags ?? null,
  };
}

// ── Main component ────────────────────────────────────────────

export function ScriptMetadataEditDialog({
  open,
  script,
  onClose,
  onSuccess,
}: ScriptMetadataEditDialogProps) {
  // ── Form state ─────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Pairing state — `selectedId` is the id the row + save payload read from.
  // Null means "no image paired" (either never had one, or the user clicked
  // Remove). The PairedImageRow renders its empty-state placeholder in that
  // case. `imageTouched` drives the three-state PUT payload: when false we
  // omit `default_image_id` so the server preserves the existing pairing;
  // when true we always send the key (int or explicit null).
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [imageTouched, setImageTouched] = useState(false);

  // Touched flags for the text fields — same three-state principle.
  const [titleTouched, setTitleTouched] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);

  // ── Image lists ────────────────────────────────────────────
  // Fetched on open. Used only to resolve `selectedId` into a
  // PlaceholderImage for the row (thumbnail + name). The picker fetches
  // its own copies independently.
  const [myImages, setMyImages] = useState<UserImage[]>([]);
  const [sharedImages, setSharedImages] = useState<LibraryImage[]>([]);
  const [imagesError, setImagesError] = useState<string | null>(null);

  // ── Picker modal ───────────────────────────────────────────
  // Opens on top of this dialog when the user clicks Change (or Pick on the
  // empty-state placeholder). MUI z-index stacking lands the picker above;
  // this dialog stays put underneath. Mirrors PublishScriptDialog.
  const [pickerOpen, setPickerOpen] = useState(false);

  // ── Save state ─────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Initialize form state every time the dialog opens. Pre-fill from
  // the current script's values so "no edit" lands as no-op.
  useEffect(() => {
    if (!open || !script) return;
    setTitle(script.title);
    setDescription(script.description ?? "");
    setSelectedId(script.default_image_id ?? null);
    setTitleTouched(false);
    setDescriptionTouched(false);
    setImageTouched(false);
    setSaveError(null);
  }, [open, script]);

  // Fetch both image lists on open so we can resolve `selectedId` into
  // a thumbnail + name for the row.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setImagesError(null);
    Promise.all([imageApi.getMyImages(), imageApi.getSharedImages()])
      .then(([mine, shared]) => {
        if (cancelled) return;
        setMyImages(mine);
        setSharedImages(shared);
      })
      .catch((err) => {
        if (cancelled) return;
        setImagesError(
          err instanceof Error ? err.message : "Failed to load images.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Resolve the current pairing into a `PlaceholderImage` for the row.
  // Returns null when nothing is paired (or the paired image is no longer
  // visible to this user) — the row renders its empty-state placeholder.
  const pairedImage = useMemo<PlaceholderImage | null>(() => {
    if (selectedId == null) return null;
    const mine = myImages.find((i) => i.id === selectedId);
    if (mine) return toPlaceholderImage(mine, "user");
    const shared = sharedImages.find((i) => i.id === selectedId);
    if (shared) return toPlaceholderImage(shared, "library");
    return null;
  }, [selectedId, myImages, sharedImages]);

  // Picker callback — fires when the user picks an image from
  // LibrarySelectModal opened on top of this dialog.
  function handlePickerSelect(image: PlaceholderImage) {
    const parsedId = parseInt(image.id, 10);
    if (Number.isNaN(parsedId)) return;
    setSelectedId(parsedId);
    setImageTouched(true);
  }

  function handleClearImage() {
    setSelectedId(null);
    setImageTouched(true);
  }

  // Build the PUT payload from touched fields only.
  function buildUpdatePayload(): UserScriptUpdatePayload {
    const payload: UserScriptUpdatePayload = {};
    if (titleTouched) {
      payload.title = title.trim();
    }
    if (descriptionTouched) {
      const trimmed = description.trim();
      payload.description = trimmed === "" ? null : trimmed;
    }
    if (imageTouched) {
      // Three-state: explicit null clears, int sets. We always send the
      // key when touched so `null` survives JSON.stringify.
      payload.default_image_id = selectedId;
    }
    return payload;
  }

  const trimmedTitle = title.trim();
  const titleEmpty = trimmedTitle.length === 0;
  const hasChanges = titleTouched || descriptionTouched || imageTouched;
  const canSave = !saving && !titleEmpty && hasChanges;

  async function handleSave() {
    if (!script || !canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildUpdatePayload();
      const updated = await userScriptsApi.update(script.id, payload);
      onSuccess(updated);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save changes.",
      );
    } finally {
      setSaving(false);
    }
  }

  const headingTitle = script
    ? `Edit "${script.title}"`
    : "Edit script details";

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
          }}
        >
          <Typography
            variant="h3"
            component="span"
            sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {headingTitle}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Update the title, description, or paired reference image. Changes
              apply immediately for everyone — the code itself stays pinned to
              the version you published.
            </Typography>

            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}

            {imagesError && (
              <Alert severity="warning" onClose={() => setImagesError(null)}>
                {imagesError}
              </Alert>
            )}

            {/* Title */}
            <TextField
              label="Title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleTouched(true);
              }}
              required
              error={titleEmpty && titleTouched}
              helperText={
                titleEmpty && titleTouched ? "Title is required." : undefined
              }
              fullWidth
              autoFocus
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave) {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />

            {/* Description */}
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescriptionTouched(true);
              }}
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              disabled={saving}
            />

            {/* Reference image — unified PairedImageRow pattern */}
            <div>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, fontWeight: 500 }}
              >
                Reference image
              </Typography>
              <PairedImageRow
                image={pairedImage}
                isTemp={false}
                onPick={saving ? undefined : () => setPickerOpen(true)}
                onClear={saving ? undefined : handleClearImage}
              />
            </div>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
            disableElevation
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image picker, stacked on top of this dialog. MUI's Dialog z-index
          handles the stacking — this dialog stays mounted underneath and
          refreshes its row when the picker fires onSelect. */}
      <LibrarySelectModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
      />
    </>
  );
}
